import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { campaignService } from '../services/campaignService';

// Global In-Memory Cache for campaigns (TTL: 60s)
const campaignsMemoryCache = new Map();
const CACHE_TTL_MS = 60 * 1000;

export const useCampaigns = () => {
  const location = useLocation();
  const [campaigns, setCampaigns] = useState([]);
  const [dismissedMap, setDismissedMap] = useState({});
  const [loading, setLoading] = useState(true);

  const currentPath = location.pathname;

  const loadActiveCampaigns = useCallback(async () => {
    const cached = campaignsMemoryCache.get(currentPath);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      setCampaigns(cached.data);
      setLoading(false);
      return;
    }

    try {
      const data = await campaignService.getActiveCampaigns(currentPath);
      const safeData = data || [];
      campaignsMemoryCache.set(currentPath, { data: safeData, timestamp: Date.now() });
      setCampaigns(safeData);
    } catch (e) {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [currentPath]);

  useEffect(() => {
    loadActiveCampaigns();
  }, [loadActiveCampaigns]);

  // Check if a campaign is dismissed by frequency rule
  const isDismissed = useCallback((campaign) => {
    if (!campaign || !campaign.dismissible) return false;
    const campaignId = campaign.id;
    const freq = campaign.frequency || 'ONCE_PER_DAY';

    if (dismissedMap[campaignId]) return true;

    try {
      if (freq === 'ONCE_PER_SESSION') {
        return sessionStorage.getItem(`datx_cmp_dismissed_${campaignId}`) === 'true';
      } else if (freq === 'ONCE_EVER') {
        return localStorage.getItem(`datx_cmp_dismissed_${campaignId}`) === 'true';
      } else if (freq === 'ONCE_PER_DAY') {
        const lastDismiss = localStorage.getItem(`datx_cmp_last_dismissed_${campaignId}`);
        if (!lastDismiss) return false;
        const diffMs = Date.now() - Number(lastDismiss);
        return diffMs < 24 * 60 * 60 * 1000; // within 24h
      }
    } catch (e) {
      return false;
    }
    return false;
  }, [dismissedMap]);

  const dismissCampaign = useCallback((campaign) => {
    if (!campaign) return;
    const campaignId = campaign.id;
    const freq = campaign.frequency || 'ONCE_PER_DAY';

    setDismissedMap((prev) => ({ ...prev, [campaignId]: true }));

    try {
      if (freq === 'ONCE_PER_SESSION') {
        sessionStorage.setItem(`datx_cmp_dismissed_${campaignId}`, 'true');
      } else if (freq === 'ONCE_EVER') {
        localStorage.setItem(`datx_cmp_dismissed_${campaignId}`, 'true');
      } else if (freq === 'ONCE_PER_DAY') {
        localStorage.setItem(`datx_cmp_last_dismissed_${campaignId}`, String(Date.now()));
      }
    } catch (e) {
      // Safe storage
    }
  }, []);

  // Top Bar Campaign (Highest priority available for TOP_BAR)
  const topBarCampaign = useMemo(() => {
    return campaigns.find((c) => c.placement === 'TOP_BAR' && !isDismissed(c)) || null;
  }, [campaigns, isDismissed]);

  // Smart Popup Campaign (Highest priority available for POPUP)
  const popupCampaign = useMemo(() => {
    return campaigns.find((c) => c.placement === 'POPUP' && !isDismissed(c)) || null;
  }, [campaigns, isDismissed]);

  // Sticky Bottom Campaign
  const stickyBottomCampaign = useMemo(() => {
    return campaigns.find((c) => c.placement === 'STICKY_BOTTOM' && !isDismissed(c)) || null;
  }, [campaigns, isDismissed]);

  // Track click & navigate
  const handleCampaignClick = useCallback((campaign) => {
    if (!campaign) return;
    campaignService.trackCampaign(campaign.id, 'click');
  }, []);

  const handleCampaignView = useCallback((campaign) => {
    if (!campaign) return;
    campaignService.trackCampaign(campaign.id, 'view');
  }, []);

  return {
    campaigns,
    topBarCampaign,
    popupCampaign,
    stickyBottomCampaign,
    loading,
    dismissCampaign,
    handleCampaignClick,
    handleCampaignView,
    refreshCampaigns: loadActiveCampaigns,
  };
};
